import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Instagram, LogOut, Mail, Menu, PencilLine, X } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider, useSite } from "@/site/SiteContext";
import "@/styles/site.css";

const NAV = [["/about", "About Us"], ["/portfolio", "Our Portfolio"], ["/contact", "Contact Us"]];
export const STUDIO_EMAIL = "haus.a.interiors@gmail.com";
export const INSTAGRAM = "https://instagram.com/haus.a.interiors";

const EditBar = () => {
  const { isAdmin, editing, toggleEditing, logout } = useSite();
  if (!isAdmin) return null;
  return (
    <div className="hs-editbar" data-testid="edit-mode-bar">
      <span><PencilLine size={14} /> {editing ? "Edit mode: click any text to edit, or use “Replace image”." : "Edit mode is paused."}</span>
      <div>
        <Link to="/admin" data-testid="edit-bar-dashboard-link">Dashboard</Link>
        <button onClick={() => toggleEditing(!editing)} data-testid="edit-bar-toggle-button">{editing ? "Exit edit mode" : "Resume editing"}</button>
        <button onClick={logout} data-testid="edit-bar-logout-button"><LogOut size={13} /> Logout</button>
      </div>
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const overHero = pathname === "/";

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`hs-header ${overHero && !scrolled ? "is-clear" : "is-solid"}`} data-testid="site-header">
      <div className="hs-header__inner">
        <Link to="/" className="hs-wordmark" data-testid="site-wordmark-home-link">HAUS A</Link>
        <nav className="hs-nav" aria-label="Main">
          {NAV.map(([to, label]) => <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}-link`}>{label}</NavLink>)}
        </nav>
        <button className="hs-burger" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button"><Menu size={22} strokeWidth={1.25} /></button>
      </div>
      {open && (
        <div className="hs-mobile-menu" data-testid="mobile-menu">
          <button className="hs-mobile-menu__close" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close-button"><X size={26} strokeWidth={1.25} /></button>
          <Link to="/" className="hs-wordmark">HAUS A</Link>
          {NAV.map(([to, label]) => <NavLink key={to} to={to} data-testid={`mobile-nav-${to.slice(1)}-link`}>{label}</NavLink>)}
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="hs-footer" data-testid="site-footer">
    <div className="hs-footer__inner">
      <Link to="/" className="hs-wordmark hs-footer__mark" data-testid="footer-wordmark">HAUS A</Link>
      <div className="hs-footer__links">
        <a href={`mailto:${STUDIO_EMAIL}`} data-testid="footer-email-link"><Mail size={14} strokeWidth={1.25} /> {STUDIO_EMAIL}</a>
        <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link"><Instagram size={14} strokeWidth={1.25} /> @haus.a.interiors</a>
      </div>
      <Link to="/admin" className="hs-footer__admin" data-testid="footer-admin-link">Admin</Link>
    </div>
  </footer>
);

const Shell = () => {
  const { content, isAdmin } = useSite();
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className={`hs ${isAdmin ? "has-editbar" : ""}`}>
      <EditBar />
      <Header />
      {content ? <main key={pathname} className="hs-page"><Outlet /></main> : <div className="hs-loading" data-testid="site-loading">HAUS A</div>}
      {content && <Footer />}
      <Toaster position="bottom-right" />
    </div>
  );
};

export default function SiteLayout() {
  return <SiteProvider><Shell /></SiteProvider>;
}
